import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, RotateCcw } from 'lucide-react'
import { ProposalDocument } from '../components/proposal-editor/ProposalDocument'
import { ChatPanel } from '../components/proposal-editor/ChatPanel'
import { ProposalHeader } from '../components/proposal-editor/ProposalHeader'
import { ProposalFooter } from '../components/proposal-editor/ProposalFooter'
import { SavingIndicator } from '../components/proposal-editor/SavingIndicator'
import { SourceStaleBanner } from '../components/proposal-editor/SourceStaleBanner'
import { useDocSync } from '../hooks/proposal-editor/useDocSync'
import { useEditorSelection } from '../hooks/proposal-editor/useEditorSelection'
import { useEditorSend } from '../hooks/proposal-editor/useEditorSend'
import { useFreezeOnAgentRun } from '../hooks/proposal-editor/useFreezeOnAgentRun'
import { useTypewriter } from '../hooks/proposal-editor/useTypewriter'
import { usePendingSaves } from '../hooks/proposal-editor/usePendingSaves'
import { patchThreadId, resetDraft } from '../components/proposal-editor/api'
import { ErrorPanel } from './PageState'
import { BankLogo } from '@/components/credem/bank-logo'
import { Button } from '@/components/ui/button'

export function ProposalRoute() {
  const { reportId } = useParams<{ reportId: string }>()
  const caseId = reportId
  const navigate = useNavigate()
  const [refetchSig, setRefetchSig] = useState<string | undefined>(undefined)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  const sync = useDocSync(caseId || '', refetchSig)
  const selection = useEditorSelection()
  const editorSend = useEditorSend()
  const freeze = useFreezeOnAgentRun()
  const tw = useTypewriter()
  const saves = usePendingSaves()

  useEffect(() => {
    if (freeze.runJustEnded) {
      const sig = `agent-run-${Date.now()}`
      setRefetchSig(sig)
    }
  }, [freeze.runJustEnded])

  const handleTextChange = useCallback(
    (blockId: string, text: string) => {
      if (!sync.draft) return
      const block = sync.draft.blocks.find((b) => b.id === blockId)
      if (!block) return
      if (!('text' in block)) return
      const updated = { ...block, text }
      void saves.track(blockId, () => sync.updateBlock(updated as typeof block))
    },
    [saves, sync],
  )

  const handlePrint = useCallback(() => {
    document.body.classList.add('body--proposal-print')
    const cleanup = () => document.body.classList.remove('body--proposal-print')
    window.addEventListener('afterprint', cleanup, { once: true })
    window.print()
  }, [])

  const handleReset = useCallback(async () => {
    if (!caseId) return
    await resetDraft(caseId)
    navigate(`/reports/${caseId}?reset=${Date.now()}`, { replace: true })
    sync.reset()
  }, [caseId, navigate, sync])

  const onArtifactRef = useCallback(
    (_artifactId: string, _docVersion: number, changedIds: string[]) => {
      tw.start(changedIds)
    },
    [tw],
  )

  const handleThreadCreated = useCallback(
    (threadId: string) => {
      if (!caseId) return
      void patchThreadId(caseId, threadId)
    },
    [caseId],
  )

  const clientMeta = useMemo(() => `Report ${caseId ?? ''} · Editor`, [caseId])

  if (!caseId) return <ErrorPanel title="Report" message="ID report mancante." />
  if (sync.loading) return null
  // threadId may legitimately be empty: the SDK creates the chat thread
  // lazily on first user send (matches the dashboard chat). We only gate
  // on draft presence, not threadId.
  if (sync.error || !sync.draft)
    return <ErrorPanel title="Report" message={sync.error ?? 'Impossibile caricare la bozza.'} />

  return (
    <div className="flex h-svh flex-col overflow-hidden bg-surface-page text-foreground">
      <header className="z-40 shrink-0 border-b border-border bg-card">
        <div className="mx-auto flex items-center gap-6 px-(--page-gutter-x) py-3 max-w-(--page-max-w)">
          <Link to="/" aria-label="Vai alla home" className="flex h-12 shrink-0 items-center">
            <BankLogo />
          </Link>
          <Link
            to="/reports"
            className="credem-shell-link hidden rounded-sm text-caption md:inline"
          >
            Home
          </Link>
        </div>
      </header>

      <div className="mx-auto grid min-h-0 w-full max-w-(--page-max-w) flex-1 grid-cols-1 min-[900px]:grid-cols-[minmax(0,1fr)_var(--rail-w)]">
        <main className="flex min-h-0 min-w-0 flex-col">
          <div className="flex min-h-[72px] shrink-0 flex-col justify-center gap-2 px-(--page-gutter-x) py-4 min-[900px]:pr-3">
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Indietro"
                className="flex size-10 shrink-0 items-center justify-center rounded-full text-foreground hover:bg-secondary"
              >
                <ArrowLeft className="size-5" aria-hidden />
              </button>
              <nav
                aria-label="Breadcrumb"
                className="flex min-w-0 items-center gap-2 text-caption text-muted-foreground"
              >
                <Link
                  to="/reports"
                  className="credem-shell-link rounded-sm"
                >
                  Report
                </Link>
                <span aria-hidden className="text-muted-foreground/60">
                  /
                </span>
                <span className="min-w-0 truncate font-bold text-foreground" aria-current="page">
                  Editor
                </span>
              </nav>
            </div>
          </div>

          <section className="flex min-h-0 flex-1 flex-col gap-(--page-gap-y) px-(--page-gutter-x) pt-px pb-(--page-gutter-y) min-[900px]:pr-3">
            <header className="flex flex-col gap-4 pt-2">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-body-default text-muted-foreground">Report</p>
                  <h1 className="truncate text-screen-title text-foreground">Proposta cliente</h1>
                  <ProposalHeader
                    clientMeta={clientMeta}
                    rightSlot={
                      <SavingIndicator
                        state={saves.state}
                        error={saves.error}
                        onRetry={() => {
                          /* retry: deferred */
                        }}
                      />
                    }
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" className="gap-1" onClick={handlePrint}>
                  <Download className="size-3.5" aria-hidden />
                  Esporta PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => void handleReset()}
                  disabled={freeze.isAgentRunning}
                >
                  <RotateCcw className="size-3.5" aria-hidden />
                  Ripristina
                </Button>
              </div>
            </header>

            {!bannerDismissed && (
              <SourceStaleBanner
                clientLabel="Client"
                sourceChanged={sync.draft.source_changed}
                onReset={() => void handleReset()}
                onDismiss={() => setBannerDismissed(true)}
              />
            )}

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-zinc-200">
              <div className="min-h-0 flex-1 overflow-auto px-4 py-6 md:px-8">
                <article className="mx-auto min-h-full max-w-[720px] bg-card px-8 py-10 text-foreground shadow-[0_1px_0_rgba(0,0,0,0.05),0_18px_40px_rgba(0,0,0,0.18)] md:px-16 md:py-14">
                  <ProposalDocument
                    draft={sync.draft}
                    selectedIds={selection.selectedIds}
                    isAgentRunning={freeze.isAgentRunning}
                    isAnimating={tw.isAnimating}
                    onTextChange={handleTextChange}
                    onToggleSelect={selection.toggle}
                    onClearSelection={selection.clear}
                    onMenuAction={(intent, blockId) => {
                      // 'delete' is a destructive local op — handle it directly via the
                      // FE-driven endpoint, not via the chat agent. All other intents
                      // round-trip through the editor agent (which reads doc state via
                      // proposal_doc_get and emits an updated doc artifact).
                      if (intent === 'delete') {
                        void sync.deleteBlock(blockId)
                        return
                      }
                      editorSend.sendEditorMessage(intent, blockId)
                    }}
                  />
                </article>
              </div>
            </div>
            <ProposalFooter
              onReset={handleReset}
              onPrint={handlePrint}
              frozen={freeze.isAgentRunning}
            />
          </section>
        </main>

        <aside className="hidden min-h-0 flex-col py-(--page-gutter-y) pl-3 pr-(--page-gutter-x) min-[900px]:flex">
          <ChatPanel
            threadId={sync.threadId}
            caseId={caseId}
            selectedBlockIds={selection.selectedIds}
            onArtifactRef={onArtifactRef}
            onRegisterSend={editorSend.registerSend}
            onRegisterFlow={freeze.registerFlow}
            onThreadCreated={handleThreadCreated}
          />
        </aside>
      </div>
    </div>
  )
}
